import { Link } from "react-router-dom";
import {
  Plane, BedDouble, Coffee, TrainFront, Map, BookOpen, Shirt, Gift, ShieldCheck, Check, ArrowRight,
} from "lucide-react";

const INCLUDED = [
  { icon: Plane, label: "Round-trip airfare" },
  { icon: BedDouble, label: "Five-star accommodation" },
  { icon: Coffee, label: "Daily breakfast" },
  { icon: TrainFront, label: "Haramain High-Speed Train" },
  { icon: Map, label: "Guided Ziyarat tours" },
  { icon: BookOpen, label: "Religious seminars & Q&A sessions" },
  { icon: Shirt, label: "Complimentary Ihram" },
  { icon: Gift, label: "Welcome gift bag" },
  { icon: ShieldCheck, label: "24/7 group support throughout your journey" },
];

const UPGRADES = [
  { label: "Triple Occupancy", price: "+$230 CAD" },
  { label: "Double Occupancy", price: "+$500 CAD" },
];

export default function Pricing() {
  return (
    <section data-testid="pricing-section" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl">
          <span className="overline">Package & Pricing</span>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl text-[color:var(--maqam-navy)] leading-[1.05] tracking-tight">
            October 2026 Umrah Package
          </h2>
          <p className="mt-5 text-[var(--maqam-muted)] leading-relaxed">
            One inclusive price, one unforgettable journey — worship, community, and care from
            departure to return.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Price card — value first */}
          <div className="lg:col-span-5">
            <div
              data-testid="pricing-card"
              className="relative rounded-3xl overflow-hidden text-white p-8 md:p-10 h-full islamic-pattern-dark review-shadow"
            >
              <span className="inline-block rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.16em] font-semibold" style={{ background: "var(--maqam-gold)", color: "var(--maqam-navy)" }}>
                Quad Occupancy · Best Value
              </span>
              <div className="mt-6">
                <p className="text-white/70 text-sm">From</p>
                <p className="font-serif text-6xl md:text-7xl leading-none mt-1" data-testid="price-regular">
                  $3,599
                  <span className="text-2xl align-top ml-1 text-white/80">CAD</span>
                </p>
                <p className="mt-2 text-white/70 text-sm">per person · all-inclusive package</p>
              </div>

              <div className="mt-8 rounded-2xl bg-white/10 border border-white/15 p-5">
                <p className="flex items-center justify-between">
                  <span className="text-white/85 text-sm">Student Price</span>
                  <span className="font-serif text-2xl text-[color:var(--maqam-gold)]" data-testid="price-student">$3,499 CAD</span>
                </p>
                <p className="mt-2 text-xs text-white/60">
                  Available to eligible students with proof of current enrolment at registration.
                </p>
              </div>

              <Link
                to="/contact"
                data-testid="pricing-register-btn"
                className="mt-8 btn-gold rounded-full px-6 py-3 font-semibold inline-flex items-center justify-center gap-2 w-full"
              >
                Register Interest <ArrowRight size={18} />
              </Link>
            </div>
          </div>

          {/* What's included */}
          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-[var(--maqam-border)] p-8 md:p-10 h-full">
              <h3 className="font-serif text-2xl md:text-3xl text-[color:var(--maqam-navy)]">What's Included</h3>
              <div className="gold-divider mt-3" />
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                {INCLUDED.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <li key={i} data-testid={`included-${i}`} className="flex items-center gap-3">
                      <span className="inline-flex w-9 h-9 rounded-full items-center justify-center flex-shrink-0" style={{ background: "rgba(200,162,74,0.16)" }}>
                        <Icon size={16} className="text-[color:var(--maqam-navy)]" />
                      </span>
                      <span className="text-sm text-[color:var(--maqam-ink)]">{item.label}</span>
                    </li>
                  );
                })}
              </ul>

              {/* Room upgrades — presented after value */}
              <div className="mt-8 pt-6 border-t border-[var(--maqam-border)]">
                <p className="text-xs uppercase tracking-[0.16em] text-[color:var(--maqam-gold-text)] font-semibold">
                  Optional Room Upgrades
                </p>
                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  {UPGRADES.map((u, i) => (
                    <div
                      key={i}
                      data-testid={`upgrade-${i}`}
                      className="flex-1 rounded-xl border border-[var(--maqam-border)] bg-[var(--maqam-surface)] px-5 py-4 flex items-center justify-between"
                    >
                      <span className="text-sm text-[color:var(--maqam-navy)] font-medium">{u.label}</span>
                      <span className="text-sm font-semibold text-[color:var(--maqam-gold-text)]">{u.price}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-xs text-[var(--maqam-muted)]">Per person, added to the base package price.</p>
              </div>
            </div>
          </div>
        </div>

        <p className="mt-6 text-xs text-[var(--maqam-muted)] max-w-3xl">
          Prices in Canadian dollars. Final pricing, availability, and booking are confirmed through our
          licensed travel partner, <span className="text-[color:var(--maqam-navy)] font-semibold">Amax Travel Canada, TICO Registration #50027257</span>.
        </p>
      </div>
    </section>
  );
}
